import { contentComponentFactory } from "$lib/content/contentComponentFactory";
import type { ConfigurationScreen } from "$lib/elev8Api/configuration";
import type { Content } from "$lib/elev8Api/contentTypes";
import { logger } from "$lib/utils/logger";

/**
 * postMessage contract between elev8-central's notice composer (parent) and
 * the /preview route (iframe). See docs/Preview.md.
 */
interface PreviewMessage {
  type: "elev8-preview";
  content: Content;
  screen?: ConfigurationScreen;
  language?: string;
}

const READY_MESSAGE = { type: "elev8-preview-ready" } as const;

// Only central may drive the preview. The API base URL is central's origin.
function allowedParentOrigins(): string[] {
  const origins = [new URL(import.meta.env.VITE_ELEV8_API_BASE_URL).origin];
  if (import.meta.env.MODE === "development") {
    origins.push("http://localhost:3000", window.location.origin);
  }
  return origins;
}

function parsePreviewMessage(
  event: MessageEvent,
  allowedOrigins: string[]
): PreviewMessage | undefined {
  if (event.source !== window.parent) return undefined;
  if (!allowedOrigins.includes(event.origin)) {
    logger.debug(`[Preview] Ignoring message from ${event.origin}`);
    return undefined;
  }
  const data: unknown = event.data;
  if (!isPreviewMessage(data)) return undefined;
  if (!contentComponentFactory.isSupported(data.content.content_type)) {
    logger.warn(
      `[Preview] Unsupported content_type: ${String(data.content.content_type)}`
    );
    return undefined;
  }
  return data;
}

function isPreviewMessage(data: unknown): data is PreviewMessage {
  if (typeof data !== "object" || data === null) return false;
  const { type, content } = data as Partial<PreviewMessage>;
  return (
    type === "elev8-preview" &&
    typeof content === "object" &&
    content !== null &&
    typeof content.content_type === "string"
  );
}

export { allowedParentOrigins, parsePreviewMessage, READY_MESSAGE };
export type { PreviewMessage };
